import React, { Suspense, useState, useEffect } from 'react'
import { LinearProgress } from '@mui/material'
import { MenuItem } from '../types/menuItem.types'
import { Route } from '../types/route.types'
import { useTranslation } from 'react-i18next'
import { ProfileIcon, SubscriptionIcon, MembersIcon, NewsletterIcon, InsightsIcon, InfoIcon, ChatIcon } from '../components/styles/Icons.styled'

/*-------------------------------------------------------------------------------
*                          LAZY-LOADED PAGES
*  Each page is loaded on demand via React.lazy() to reduce the initial bundle.
*  Public, Member, and Admin pages are split into separate chunks.
*  Each page is wrapped in its own Suspense boundary so the layout (header, menus)
*  stays mounted while only the page content shows a loading bar.
-------------------------------------------------------------------------------*/

// Loading fallback that only shows a progress bar after a delay to avoid flicker on fast loads
const LOADER_DELAY_MS = 300

const PageLoadingFallback = () => {
  const [showLoader, setShowLoader] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowLoader(true), LOADER_DELAY_MS)
    return () => clearTimeout(timer)
  }, [])

  if (!showLoader) return null

  return <LinearProgress sx={{ height: '5px' }} />
}

// Wraps a lazy-loaded page in its own Suspense boundary
const lazyPage = (factory: () => Promise<{ default: React.ComponentType }>) => {
  const LazyComponent = React.lazy(factory)
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <LazyComponent />
    </Suspense>
  )
}

// Public Pages
const LoginPage = lazyPage(() => import('../pages/LoginPage/LoginPage'))
const ForgotPasswordPage = lazyPage(() => import('../pages/ForgotPasswordPage/ForgotPasswordPage'))
const ResetPasswordPage = lazyPage(() => import('../pages/ResetPasswordPage/ResetPasswordPage'))
const CompleteCustomInvitePage = lazyPage(() => import('../pages/CompleteInvitePage/CompleteInvitePage'))

// Member Pages
const NewsPage = lazyPage(() => import('../pages/NewsPage/NewsPage'))
const MemberPostPage = lazyPage(() => import('../pages/MemberPostPage/MemberPostPage'))
const HomePage = lazyPage(() => import('../pages/HomePage/HomePage'))
const MemberProfilePage = lazyPage(() => import('../pages/MemberProfilePage/MemberProfilePage'))
const AccountProfilePage = lazyPage(() => import('../pages/AccountProfilePage/AccountProfilePage'))
const AccountSubscriptionPage = lazyPage(() => import('../pages/AccountSubscriptionPage/AccountSubscriptionPage'))

// Admin Pages
const AdminMembersPage = lazyPage(() => import('../pages/AdminMembersPage/AdminMembersPage'))
const AdminMemberManagementPage = lazyPage(() => import('../pages/AdminMemberManagementPage/AdminMemberManagementPage'))
const MemberSubscriptionPage = lazyPage(() => import('../pages/MemberSubscriptionPage/MemberSubscriptionPage'))
const NewsletterPage = lazyPage(() => import('../pages/NewsletterPage/NewsletterPage'))
const InsightsPage = lazyPage(() => import('../pages/InsightsPage/InsightsPage'))
const InfoPage = lazyPage(() => import('../pages/InfoPage/InfoPage'))

/*-------------------------------------------------------------------------------
*                               ROUTES
-------------------------------------------------------------------------------*/

//---------------------------- Common Routes ----------------------------
export const MemberRoutes = {
  HOME: '/news',
  MEMBERS: '/members',
  POST: '/news/:postId',
  NEWS_UNAVAILABLE: '/news/*',
  MEMBER_PROFILE: '/members/:memberId',
  ACCOUNT_PROFILE: '/account/profile',
  ACCOUNT_SUBSCRIPTION: '/account/subscription',
} as const

//---------------------------- ADMIN Routes ----------------------------
export const AdminRoutes = {
  MEMBERS: '/admin/members',
  MEMBER_MANAGEMENT: '/admin/members/:memberId',
  MEMBER_SUBSCRIPTION: '/admin/members/:memberId/subscription',
  NEWSLETTER: '/admin/newsletter',
  INSIGHTS: '/admin/insights',
  INFO: '/admin/info',
} as const

//---------------------------- PUBLIC Routes ----------------------------
export enum PublicRoutes {
  LOGIN = '/login',
  FORGOT_PASSWORD = '/forgot-password',
  RESET_PASSWORD = '/reset-password/:tokenId',
  COMPLETE_INVITE = '/complete-invite/:tokenId',
}

/*-------------------------------------------------------------------------------
*                               ROUTES MAPPING
-------------------------------------------------------------------------------*/

//---------------------------- PUBLIC Routes  Mapping ----------------------------
export const publicRoutesConfig: Route[] = [
  { path: PublicRoutes.LOGIN, element: LoginPage },
  { path: PublicRoutes.FORGOT_PASSWORD, element: ForgotPasswordPage },
  { path: PublicRoutes.RESET_PASSWORD, element: ResetPasswordPage },
  { path: PublicRoutes.COMPLETE_INVITE, element: CompleteCustomInvitePage },
]

//---------------------------- CUSTOMER Routes  Mapping ----------------------------
export const memberRoutesConfig: Route[] = [
  { path: MemberRoutes.HOME, element: NewsPage },
  { path: MemberRoutes.POST, element: MemberPostPage },
  { path: MemberRoutes.NEWS_UNAVAILABLE, element: MemberPostPage },
  { path: MemberRoutes.MEMBERS, element: HomePage },
  { path: MemberRoutes.MEMBER_PROFILE, element: MemberProfilePage },
  { path: MemberRoutes.ACCOUNT_PROFILE, element: AccountProfilePage },
  { path: MemberRoutes.ACCOUNT_SUBSCRIPTION, element: AccountSubscriptionPage },
]

//---------------------------- ADMIN Routes Mapping ----------------------------
export const adminRoutesConfig: Route[] = [
  { path: AdminRoutes.MEMBERS, element: AdminMembersPage },
  { path: AdminRoutes.MEMBER_MANAGEMENT, element: AdminMemberManagementPage },
  { path: AdminRoutes.MEMBER_SUBSCRIPTION, element: MemberSubscriptionPage },
  { path: AdminRoutes.NEWSLETTER, element: NewsletterPage },
  { path: AdminRoutes.INSIGHTS, element: InsightsPage },
  { path: AdminRoutes.INFO, element: InfoPage },
]

/*-------------------------------------------------------------------------------
*                                MENUS
-------------------------------------------------------------------------------*/

//---------------------------- Member Menu Items ----------------------------
export const useMemberMenuItems = (): MenuItem[] => {
  const { t } = useTranslation()
  return [
    { label: t('news.title'), url: MemberRoutes.HOME, icon: <ChatIcon /> },
    { label: t('menu.member.home'), url: MemberRoutes.MEMBERS, icon: <MembersIcon /> },
    { label: t('menu.member.account.profile'), url: MemberRoutes.ACCOUNT_PROFILE, icon: <ProfileIcon />, section: 'avatar' },
    { label: t('menu.member.account.subscription'), url: MemberRoutes.ACCOUNT_SUBSCRIPTION, icon: <SubscriptionIcon />, section: 'avatar' },
  ]
}
//---------------------------- ADMIN Menu Items ----------------------------
export const useAdminMenuItems = (): MenuItem[] => {
  const { t } = useTranslation()
  return [
    { label: t('menu.admin.members'), url: AdminRoutes.MEMBERS, icon: <MembersIcon /> },
    { label: t('menu.admin.newsletter'), url: AdminRoutes.NEWSLETTER, icon: <NewsletterIcon /> },
    { label: t('menu.admin.insights'), url: AdminRoutes.INSIGHTS, icon: <InsightsIcon /> },
    { label: t('menu.admin.info'), url: AdminRoutes.INFO, icon: <InfoIcon /> },
  ]
}
